// Workstream: shop. Orders, fulfilment status, print files and products.
import { useCallback, useEffect, useState } from 'react';
import type { OrderStatus, ShopProduct } from '@shared/types';
import { Sheet } from '../../components/Sheet';
import { usePrefs } from '../../components/Prefs';
import { STATUS_LABELS, formatPrice, shopAdmin, type AdminOrder, type OrderFilter } from '../shop/api';

const FILTERS: { key: OrderFilter; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'paid', label: 'To print' },
  { key: 'in_production', label: 'Printing' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'preorder', label: 'Pre-orders' },
  { key: 'pending', label: 'Pending' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'refunded', label: 'Refunded' },
];

const STATUS_CHIP: Record<OrderStatus, string> = {
  pending: '',
  paid: 'magenta',
  in_production: 'orange',
  shipped: 'blue',
  cancelled: 'red',
  refunded: 'red',
};

/** The usual next step for an order, shown as a one-tap button. */
const NEXT: Partial<Record<OrderStatus, OrderStatus>> = { paid: 'in_production', in_production: 'shipped' };

function StatusChip({ order }: { order: Pick<AdminOrder, 'status' | 'preorder'> }) {
  if (order.preorder && order.status === 'pending') return <span className="chip orange">pre-order</span>;
  return <span className={`chip ${STATUS_CHIP[order.status]}`}>{STATUS_LABELS[order.status].toLowerCase()}</span>;
}

export function OrdersTab() {
  const [section, setSection] = useState<'orders' | 'products'>('orders');
  return (
    <section className="stack orders-tab">
      <div className="segmented" style={{ maxWidth: 320 }}>
        <button type="button" aria-pressed={section === 'orders'} onClick={() => setSection('orders')}>
          Orders
        </button>
        <button type="button" aria-pressed={section === 'products'} onClick={() => setSection('products')}>
          Products
        </button>
      </div>
      {section === 'orders' ? <Orders /> : <Products />}
    </section>
  );
}

// ------------------------------------------------------------------ orders

function Orders() {
  const [filter, setFilter] = useState<OrderFilter>('');
  const [orders, setOrders] = useState<AdminOrder[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const { toast } = usePrefs();

  const load = useCallback(async () => {
    try {
      const res = await shopAdmin.orders(filter);
      setOrders(res.orders);
      setCounts(res.counts);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  }, [filter, toast]);

  useEffect(() => {
    setOrders(null);
    void load();
  }, [load]);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <>
      <div className="stat-grid">
        <div className="stat">
          <div className="n">{counts.paid ?? 0}</div>
          <div className="l">Paid, waiting to print</div>
        </div>
        <div className="stat">
          <div className="n">{counts.in_production ?? 0}</div>
          <div className="l">At the printer</div>
        </div>
        <div className="stat">
          <div className="n">{counts.shipped ?? 0}</div>
          <div className="l">Shipped</div>
        </div>
        <div className="stat">
          <div className="n">{total}</div>
          <div className="l">All orders</div>
        </div>
      </div>
      <div className="filter-chips" role="group" aria-label="Filter orders">
        {FILTERS.map((f) => (
          <button key={f.key || 'all'} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
            {f.key && f.key !== 'preorder' && counts[f.key] ? <span className="count">{counts[f.key]}</span> : null}
          </button>
        ))}
      </div>
      {!orders && <p className="muted">Loading...</p>}
      {orders?.length === 0 && <p className="muted">No orders here yet.</p>}
      {orders && orders.length > 0 && (
        <div className="table-wrap">
          <table className="table orders-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Deck</th>
                <th>Customer</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="clickable" onClick={() => setOpenId(o.id)}>
                  <td>
                    <button type="button" className="linkish" onClick={() => setOpenId(o.id)}>
                      {o.publicId}
                    </button>
                    <div className="muted small">{new Date(o.createdAt).toLocaleDateString()}</div>
                    <StatusChip order={o} />
                  </td>
                  <td>
                    {o.product.name}
                    <div className="muted small">
                      × {o.quantity} · {formatPrice(o.amountCents, o.currency)}
                      {o.customPackCode ? ` · pack ${o.customPackCode}` : ''}
                    </div>
                  </td>
                  <td className="customer-cell">
                    {o.customerName || <span className="muted">(not yet)</span>}
                    <div className="muted small">{o.email}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openId && (
        <OrderDetail
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
        />
      )}
    </>
  );
}

function Address({ order }: { order: AdminOrder }) {
  const s = order.shipping;
  if (!s) return <p className="muted small">No shipping address yet. Stripe collects it at checkout.</p>;
  const a = s.address;
  return (
    <address className="ship-address">
      <strong>{s.name}</strong>
      <br />
      {a.line1}
      {a.line2 && (
        <>
          <br />
          {a.line2}
        </>
      )}
      <br />
      {[a.city, a.state, a.postal_code].filter(Boolean).join(', ')}
      <br />
      {a.country}
      {s.phone && (
        <>
          <br />
          <a href={`tel:${s.phone}`}>{s.phone}</a>
        </>
      )}
    </address>
  );
}

function OrderDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [order, setOrder] = useState<AdminOrder | null>(null);
  const [status, setStatus] = useState<OrderStatus>('pending');
  const [tracking, setTracking] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState('');
  const { toast } = usePrefs();

  const apply = useCallback((o: AdminOrder) => {
    setOrder((prev) => ({ ...o, customPack: o.customPack ?? prev?.customPack }));
    setStatus(o.status);
    setTracking(o.tracking);
    setNotes(o.notes);
  }, []);

  useEffect(() => {
    shopAdmin.order(id).then(apply).catch((e) => toast((e as Error).message, 'error'));
  }, [id, apply, toast]);

  async function save(patch: { status?: OrderStatus; tracking?: string; notes?: string }) {
    setBusy('save');
    try {
      apply(await shopAdmin.updateOrder(id, patch));
      onChanged();
      toast('Saved.', 'success');
    } catch (e) {
      toast((e as Error).message, 'error');
    }
    setBusy('');
  }

  async function print(opts: { bleed?: boolean; backs?: boolean }, key: string) {
    if (!order) return;
    setBusy(key);
    try {
      const { pages, warnings } = await shopAdmin.downloadPrint(order, opts);
      toast(`${pages} pages downloaded.${warnings ? ' Check the first page for warnings.' : ''}`, warnings ? 'info' : 'success');
    } catch (e) {
      toast((e as Error).message, 'error');
    }
    setBusy('');
  }

  const next = order ? NEXT[order.status] : undefined;
  const dirty = order && (status !== order.status || tracking !== order.tracking || notes !== order.notes);
  return (
    <Sheet title={order ? `Order ${order.publicId}` : 'Order'} onClose={onClose}>
      {!order ? (
        <p className="muted">Loading...</p>
      ) : (
        <div className="stack order-detail">
          <div className="row between">
            <StatusChip order={order} />
            <span className="muted small">
              {new Date(order.createdAt).toLocaleString()}
              {order.updatedAt !== order.createdAt && ` · updated ${new Date(order.updatedAt).toLocaleDateString()}`}
            </span>
          </div>

          <div className="panel stack">
            <div className="row between">
              <strong>
                {order.product.name} × {order.quantity}
              </strong>
              <strong>{formatPrice(order.amountCents, order.currency)}</strong>
            </div>
            <span className="muted small">Packs: {order.product.packSlugs.join(', ') || 'none'}</span>
            {order.customPackCode && (
              <span className="small">
                Custom pack:{' '}
                <a href={`/packs/${order.customPackCode}`} target="_blank" rel="noreferrer">
                  {order.customPackCode}
                </a>
                {order.customPack
                  ? ` · ${order.customPack.name} · ${order.customPack.cardCount} cards${order.customPack.photoCount ? `, ${order.customPack.photoCount} photos` : ''}`
                  : ' · pack no longer exists'}
                {order.customPack && !order.customPack.ok && <span className="error"> · below {order.customPack.minCards} cards now</span>}
              </span>
            )}
          </div>

          <div className="grid-2">
            <div className="stack" style={{ gap: 6 }}>
              <strong>Ship to</strong>
              <Address order={order} />
            </div>
            <div className="stack" style={{ gap: 6 }}>
              <strong>Contact</strong>
              {order.customerName && <span>{order.customerName}</span>}
              {order.email ? <a href={`mailto:${order.email}?subject=Your CompleteMasti order ${order.publicId}`}>{order.email}</a> : <span className="muted">No email yet</span>}
              {order.stripePaymentIntent && <span className="muted small">Payment {order.stripePaymentIntent}</span>}
              {order.preorder && <span className="muted small">Pre-order: no payment taken. Email them when decks ship.</span>}
            </div>
          </div>

          <div className="panel stack">
            <strong>Print file</strong>
            <span className="muted small">Letter, 9 poker cards a sheet, crop marks. Page 1 is a job sheet for the packer.</span>
            <div className="row">
              <button type="button" className="btn btn-sm btn-blue" disabled={Boolean(busy)} onClick={() => print({}, 'pdf')}>
                {busy === 'pdf' ? 'Making PDF...' : 'Download PDF'}
              </button>
              <button type="button" className="btn btn-sm btn-outline" disabled={Boolean(busy)} onClick={() => print({ bleed: true }, 'bleed')}>
                {busy === 'bleed' ? 'Making PDF...' : 'With bleed'}
              </button>
              <button type="button" className="btn btn-sm btn-outline" disabled={Boolean(busy)} onClick={() => print({ bleed: true, backs: true }, 'backs')}>
                {busy === 'backs' ? 'Making PDF...' : 'Bleed + backs'}
              </button>
            </div>
          </div>

          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void save({ status, tracking, notes });
            }}
          >
            {next && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={Boolean(busy) || (next === 'shipped' && !tracking.trim())}
                onClick={() => void save({ status: next, tracking, notes })}
              >
                Mark {STATUS_LABELS[next].toLowerCase()}
              </button>
            )}
            <div className="grid-2">
              <label className="field">
                Status
                <select className="input" value={status} onChange={(e) => setStatus(e.target.value as OrderStatus)}>
                  {(Object.keys(STATUS_LABELS) as OrderStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Tracking
                <input className="input" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Carrier and number" maxLength={200} />
                {next === 'shipped' && !tracking.trim() && <span className="hint">Needed before you mark it shipped.</span>}
              </label>
            </div>
            <label className="field">
              Notes <span className="hint">Only admins see these.</span>
              <textarea className="input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ minHeight: 80 }} />
            </label>
            <button className="btn btn-outline" disabled={!dirty || Boolean(busy)}>
              {busy === 'save' ? 'Saving...' : 'Save changes'}
            </button>
            <a className="small" href={`/shop/thanks?order=${order.publicId}`} target="_blank" rel="noreferrer">
              Customer's order page
            </a>
          </form>
        </div>
      )}
    </Sheet>
  );
}

// ------------------------------------------------------------------ products

function Products() {
  const [products, setProducts] = useState<ShopProduct[] | null>(null);
  const { toast } = usePrefs();

  const load = useCallback(async () => {
    try {
      setProducts(await shopAdmin.products());
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!products) return <p className="muted">Loading...</p>;
  return (
    <div className="stack">
      <p className="muted small" style={{ margin: 0 }}>
        Prices are per deck in {products[0]?.currency.toUpperCase() ?? 'USD'}. Card counts come from the packs, so edits to packs show up here
        and in the shop automatically.
      </p>
      {products.map((p) => (
        <ProductEditor key={`${p.id}-${p.priceCents}-${p.active}-${p.name}`} product={p} onSaved={load} />
      ))}
    </div>
  );
}

function ProductEditor({ product, onSaved }: { product: ShopProduct; onSaved: () => void }) {
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description);
  const [price, setPrice] = useState((product.priceCents / 100).toFixed(2));
  const [active, setActive] = useState(product.active);
  const [busy, setBusy] = useState(false);
  const { toast } = usePrefs();

  const cents = Math.round(Number(price) * 100);
  const valid = name.trim() && Number.isFinite(cents) && cents >= 0;
  const dirty = name !== product.name || description !== product.description || cents !== product.priceCents || active !== product.active;

  return (
    <form
      className={`panel stack product-editor ${product.active ? '' : 'inactive'}`}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!valid) return;
        setBusy(true);
        try {
          await shopAdmin.updateProduct(product.id, { name, description, priceCents: cents, active });
          toast('Product saved.', 'success');
          onSaved();
        } catch (err) {
          toast((err as Error).message, 'error');
        }
        setBusy(false);
      }}
    >
      <div className="row between">
        <span className="row">
          <strong>{product.slug}</strong>
          <span className="chip">{product.kind === 'custom_deck' ? 'custom deck' : 'printed deck'}</span>
        </span>
        <label className="check">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          On sale
        </label>
      </div>
      <div className="grid-2">
        <label className="field">
          Name
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required />
        </label>
        <label className="field">
          Price per deck
          <input className="input" type="number" min="0" step="0.01" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
      </div>
      <label className="field">
        Description
        <textarea className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={600} rows={3} style={{ minHeight: 92 }} />
      </label>
      <div className="row between">
        <span className="muted small">
          {product.packs.map((pk) => `${pk.name} ${pk.promptCount + pk.answerCount}`).join(' + ') || 'No packs'}
          {' · '}
          {product.cardCount} cards
        </span>
        <button className="btn btn-sm btn-primary" disabled={!dirty || !valid || busy}>
          {busy ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}
